// src/components/LoadingSpinner.js - Enhanced Version (RESPONSIVE)

import React from 'react';
import { Loader2 } from 'lucide-react';

export default function LoadingSpinner({ size = 'md', fullScreen = false, text = 'Loading...' }) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  const spinner = (
    <div className="relative">
      {/* Outer ring with gradient */}
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-1 animate-spin`}>
        <div className="w-full h-full bg-white dark:bg-gray-900 rounded-full"></div>
      </div>
      
      {/* Pulsing glow effect */}
      <div className={`absolute inset-0 ${sizeClasses[size]} rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-20 blur-xl animate-pulse`}></div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20 backdrop-blur-sm flex items-center justify-center z-50">
        {/* Animated background circles - hide on mobile */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full opacity-20 blur-3xl animate-pulse hidden sm:block"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400 rounded-full opacity-20 blur-3xl animate-pulse hidden sm:block" style={{ animationDelay: '1s' }}></div>
        
        <div className="text-center relative z-10 animate-fadeIn px-4">
          {/* Logo with spinner - responsive sizing */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              {/* Rotating gradient ring - smaller on mobile */}
              <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-1.5 animate-spin">
                <div className="w-full h-full bg-white dark:bg-gray-900 rounded-full flex items-center justify-center">
                  <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    N
                  </span>
                </div>
              </div>
              
              {/* Pulsing glow */}
              <div className="absolute inset-0 w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-30 blur-2xl animate-pulse"></div>
            </div>
          </div>
          
          {/* Loading text with gradient - responsive sizing */}
          <div className="mb-4">
            <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              {text}
            </h2>
            
            {/* Animated dots */}
            <div className="flex justify-center gap-2 mt-4">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-600 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-pink-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
          
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Please wait while we load your content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      {spinner}
      {text && (
        <p className={`${textSizeClasses[size]} text-gray-600 dark:text-gray-400 font-medium`}>
          {text}
        </p>
      )}
    </div>
  );
}

// Alternative modern spinner component
export function ModernSpinner({ size = 'md', color = 'blue' }) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const colorClasses = {
    blue: 'from-blue-600 to-cyan-600',
    purple: 'from-purple-600 to-pink-600',
    green: 'from-green-600 to-emerald-600',
    gradient: 'from-blue-600 via-purple-600 to-pink-600'
  };

  return (
    <div className="relative">
      <Loader2 className={`${sizeClasses[size]} text-blue-600 animate-spin`} />
      <div className={`absolute inset-0 ${sizeClasses[size]} rounded-full bg-gradient-to-r ${colorClasses[color]} opacity-20 blur-lg animate-pulse`}></div>
    </div>
  );
}

// Dots loader component
export function DotsLoader({ size = 'md' }) {
  const dotSizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <div className="flex gap-2">
      <div className={`${dotSizeClasses[size]} bg-gradient-to-r from-blue-600 to-blue-700 rounded-full animate-bounce`}></div>
      <div className={`${dotSizeClasses[size]} bg-gradient-to-r from-purple-600 to-purple-700 rounded-full animate-bounce`} style={{ animationDelay: '0.2s' }}></div>
      <div className={`${dotSizeClasses[size]} bg-gradient-to-r from-pink-600 to-pink-700 rounded-full animate-bounce`} style={{ animationDelay: '0.4s' }}></div>
    </div>
  );
}

// Progress bar loader
export function ProgressLoader({ progress = 0 }) {
  return (
    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden shadow-inner">
      <div 
        className="h-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
        style={{ width: `${progress}%` }}
      >
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
      </div>
    </div>
  );
}

// Skeleton loader
export function SkeletonLoader({ type = 'text', count = 1 }) {
  const skeletons = {
    text: <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>,
    heading: <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4"></div>,
    avatar: <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>,
    card: <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse"></div>,
    button: <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-32"></div>
  };

  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.1}s` }}>
          {skeletons[type]}
        </div>
      ))}
    </div>
  );
}